import React, { useState, useMemo } from 'react';
import { calculateMortgage, formatCurrency } from '../utils/calculations';

export default function MortgageCalculator({ initialPrice = 0, initialRate = 7.5, initialTerm = 30, initialDown = 20 }) {
  const [price, setPrice] = useState(initialPrice || 250000);
  const [downPct, setDownPct] = useState(initialDown || 20);
  const [rate, setRate] = useState(initialRate || 7.5);
  const [term, setTerm] = useState(initialTerm || 30);

  const loanAmount = price * (1 - downPct / 100);
  const result = useMemo(() => calculateMortgage(loanAmount, rate, term), [loanAmount, rate, term]);

  const amortTable = useMemo(() => {
    if (!loanAmount || !rate || !term) return [];
    const monthlyRate = rate / 100 / 12;
    let balance = loanAmount;
    const rows = [];

    for (let year = 1; year <= Math.min(term, 10); year++) {
      let yearInterest = 0;
      let yearPrincipal = 0;
      for (let m = 0; m < 12; m++) {
        const interest = balance * monthlyRate;
        const principal = result.monthly - interest;
        yearInterest += interest;
        yearPrincipal += principal;
        balance -= principal;
      }
      rows.push({
        year,
        payment: result.monthly * 12,
        principal: yearPrincipal,
        interest: yearInterest,
        balance: Math.max(0, balance)
      });
    }
    return rows;
  }, [loanAmount, rate, term, result.monthly]);

  return (
    <div>
      {/* Inputs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="form-group">
          <label>Home Price</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
            <input
              type="number"
              className="input"
              value={price}
              onChange={e => setPrice(Number(e.target.value))}
              style={{ paddingLeft: 26 }}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Down Payment: {downPct}%</label>
          <input
            type="range"
            min="0" max="100" step="5"
            value={downPct}
            onChange={e => setDownPct(Number(e.target.value))}
            style={{ width: '100%', marginTop: 8 }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            <span>$0</span>
            <span style={{ color: 'var(--primary-light)', fontWeight: 600 }}>{formatCurrency(price * downPct / 100)}</span>
            <span>{formatCurrency(price)}</span>
          </div>
        </div>

        <div className="form-group">
          <label>Interest Rate: {rate}%</label>
          <input
            type="range"
            min="2" max="15" step="0.125"
            value={rate}
            onChange={e => setRate(Number(e.target.value))}
            style={{ width: '100%', marginTop: 8 }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            <span>2%</span>
            <span style={{ color: 'var(--primary-light)', fontWeight: 600 }}>{rate}%</span>
            <span>15%</span>
          </div>
        </div>

        <div className="form-group">
          <label>Loan Term</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[10, 15, 20, 30].map(t => (
              <button
                key={t}
                onClick={() => setTerm(t)}
                style={{
                  flex: 1, padding: '10px 0',
                  border: `1.5px solid ${term === t ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                  fontWeight: 600, fontSize: '0.85rem',
                  background: term === t ? 'rgba(37,99,235,0.15)' : 'transparent',
                  color: term === t ? 'var(--primary-light)' : 'var(--text-muted)'
                }}
              >
                {t}yr
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <div style={{ background: 'var(--bg-dark)', borderRadius: 10, padding: '16px', textAlign: 'center', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Loan Amount</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--text-primary)' }}>{formatCurrency(loanAmount)}</div>
        </div>
        <div style={{ background: 'rgba(37,99,235,0.1)', borderRadius: 10, padding: '16px', textAlign: 'center', border: '1.5px solid var(--primary)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Monthly Payment</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--primary-light)' }}>{formatCurrency(result.monthly)}</div>
        </div>
        <div style={{ background: 'var(--bg-dark)', borderRadius: 10, padding: '16px', textAlign: 'center', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Total Interest</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#ef4444' }}>{formatCurrency(result.totalInterest)}</div>
        </div>
      </div>

      {/* Amortization Table */}
      {amortTable.length > 0 && (
        <div>
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>
            Amortization (first {amortTable.length} years)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Year', 'Annual Payment', 'Principal', 'Interest', 'Balance'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {amortTable.map(row => (
                  <tr key={row.year} style={{ borderBottom: '1px solid rgba(51,65,85,0.5)' }}>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-muted)' }}>Yr {row.year}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-secondary)' }}>{formatCurrency(row.payment)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: '#10b981' }}>{formatCurrency(row.principal)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: '#ef4444' }}>{formatCurrency(row.interest)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(row.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
